/**
 * @nirvikailabs/brand — shared brand icons.
 *
 * Single source of truth for every Nirvik surface. If the mark evolves,
 * edit here and every site picks it up on next build.
 */

import type { SVGProps } from 'react';

/**
 * Trinetra brand icon — radar sweep.
 *
 * Composition (outside → in, back → front):
 *   1. Outer ring   — r=10, static.
 *   2. Middle ring  — r=6.25, static (radar range mark).
 *   3. Radar sweep  — rotating wedge (translucent fill 18% + bright
 *                     leading edge) spanning ~30° from 12 o'clock. The
 *                     "third eye" actively scanning.
 *   4. Iris ring    — r=2.5, static, sits on top of sweep.
 *
 * Animation driven by `.trinetra-sweep` in brand.css (respects
 * `prefers-reduced-motion`).
 *
 * Accepts all standard lucide-react SVGProps — drop-in everywhere.
 */
export function TrinetraLoop3(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6.25" />
      <g className="trinetra-sweep">
        <path
          d="M 12 9.5 L 12 2.25 A 9.75 9.75 0 0 1 16.875 3.56 L 13.25 9.835 A 2.5 2.5 0 0 0 12 9.5 Z"
          fill="currentColor"
          fillOpacity="0.18"
          stroke="none"
        />
        <line x1="12" y1="9.5" x2="12" y2="2.25" />
      </g>
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

/**
 * Static favicon-ready SVG string (radar sweep frozen at 12 o'clock).
 * Browsers render favicon animations unreliably, so we ship a still.
 * Color is burnt turmeric `#8D5A12` which reads on light + dark tabs.
 *
 * Consumed by each site's build step to produce `public/favicon.svg`.
 */
export const TRINETRA_FAVICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#8D5A12" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10"/>
  <circle cx="12" cy="12" r="6.25"/>
  <path d="M 12 9.5 L 12 2.25 A 9.75 9.75 0 0 1 16.875 3.56 L 13.25 9.835 A 2.5 2.5 0 0 0 12 9.5 Z" fill="#8D5A12" fill-opacity="0.22" stroke="none"/>
  <line x1="12" y1="9.5" x2="12" y2="2.25"/>
  <circle cx="12" cy="12" r="2.5"/>
</svg>`;
